import * as React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { OptionType } from "./ResourcesAutocompleteMI";
import { Button, Checkbox, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Theme } from '@material-ui/core';
import { useGetResourceGridApi, IResourceData } from '../services/useResourcesForGridApi';
import Highlight from "../../common/utils/Highlight";

import SearchIcon from '@material-ui/icons/Search';
import ZoomInIcon from '@material-ui/icons/ZoomIn';

import './ResourcesAutocompleteSearch.css';

import useDebounce from '../utils/useDebounce';
import strings from '../utils/StringManager'
import LogCompanyTooltip from './LogCompanyTooltip';

type IResourceDataEx = IResourceData & {
    highlights: { [key: string]: string }
}

function SearchButton(props: { onClick, title: string, className: string }) {
    return <div className={props.className}>
        <IconButton {...props} className={"mi-icon-button"} style={{ padding: 3 }}>
            <ZoomInIcon style={{ fontSize: 20 }} />
        </IconButton>
    </div>
}

type Order = 'asc' | 'desc';

export interface HeadCell {
    id: string;
    label: string;
    numeric: boolean;
    searched: boolean;
    className?: string;
    minWidth?: number,
}

interface ITableSortLabelExProps {
    classes: ReturnType<typeof useStyles>,
    label: string,
    order: Order,
    orderBy: string,
    id: string,
    createSortHandler,
}

function TableSortLabelEx(props: ITableSortLabelExProps) {
    return <TableSortLabel
        active={props.orderBy === props.id}
        direction={props.orderBy === props.id ? props.order : 'asc'}
        onClick={props.createSortHandler(props.id)}
    >
        {props.label}
        {props.orderBy === props.id ? (
            <span className={props.classes.visuallyHidden}>
                {props.order === 'desc' ? strings.get('sortedDescending') : strings.get('sortedAscending')}
            </span>
        ) : null}
    </TableSortLabel>
}

interface EnhancedTableProps {
    classes: ReturnType<typeof useStyles>;
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
    onSelectAllClick: (event: { indeterminate: boolean,  selectedAll: boolean }) => void;
    order: Order;
    orderBy: string;
    rowCount: number;

    headCells: HeadCell[],
    multiple: boolean,

    isLoading: boolean,
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };
    let indeterminate = numSelected > 0 && numSelected < rowCount;
    let selectedAll = rowCount > 0 && numSelected === rowCount;
   
    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    {props.multiple && <Checkbox
                        indeterminate={indeterminate}
                        checked={selectedAll}
                        onClick={(e) => onSelectAllClick({
                            indeterminate,
                            selectedAll,
                        })}
                        color="primary"
                    />}
                </TableCell>
                {props.headCells?.map((headCell, i) => (
                    <TableCell
                        padding={i == 0 ? 'none': undefined}
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        style={{ minWidth: headCell.minWidth }}
                    >
                        <TableSortLabelEx
                            classes={classes}
                            createSortHandler={createSortHandler}
                            id={headCell.id}
                            label={headCell.label}
                            order={order}
                            orderBy={orderBy}
                        />
                    </TableCell>
                ))}
            </TableRow>
            {props.isLoading && <TableRow>
                <TableCell colSpan={props.headCells?.length + 1} padding="none">
                    <LinearProgress />
                </TableCell>
            </TableRow>}
        </TableHead>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
        container: {
            maxHeight: 600,
        },
    }),
);

function DisplayCell(props: { row: IResourceDataEx, column: HeadCell})
{
    let content = (<span>{props.row[props.column.id]}</span>);

    if (props.row.highlights[props.column.id])
        content = <div dangerouslySetInnerHTML={{ __html: props.row.highlights[props.column.id] }} />
   
    if (props.column.id === 'company') {
        return <LogCompanyTooltip value={props.row['company']}>
            {content}
        </LogCompanyTooltip>
    }
    return content;
}

interface IEnhancedTableProps {
    selected: { [key: number]: OptionType },
    setSelected: (value: { [key: number]: OptionType }) => void,

    page: number,
    setPage: (value: number) => void,

    filters: string,
    kind: 'Application' | 'ServiceAsset' | 'BusinessProcess' | 'DataSet' | 'Employee' | 'PersonProfile',
    multiple?: boolean,
    open: boolean,
    onClose: () => void,

    columns: HeadCell[],
    search: string,

    rowsPerPageOptions: Array<number | { value: number; label: string } >
}

function EnhancedTable(props: IEnhancedTableProps) {
    const classes = useStyles();
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<string>('name');
    
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(() => {
        if (!props.rowsPerPageOptions || props.rowsPerPageOptions.length == 0)
            return 10;

        var first = props.rowsPerPageOptions[0];
        if (typeof (first) === "number")
            return first;
        if (typeof (first) === "object")
            return first.value;

        return 10;
    });

    const [setRequest, data, isLoading, isError] = useGetResourceGridApi();
    const [rows, setSetRows] = React.useState<IResourceDataEx[]>([]);

    React.useEffect(() => {
        if (!props.open || !props.columns)
            return;

        let searchFields = props.columns.filter(x => x.searched).map(x => x.id);

        setRequest({
            dir: order == 'asc' ? 'Asc' : 'Desc',
            orderBy: orderBy,

            kind: props.kind,
            take: rowsPerPage,
            skip: props.page * rowsPerPage,
            searchFields: searchFields,
            search: props.search,
            filters: props.filters,
        })

    }, [props.open, props.page, rowsPerPage, order, orderBy, props.search, props.kind]);

    React.useEffect(() => {

        if (!data)
            return;

        if (!data.rows) {
            setSetRows([]);
            return;
        }

        setSetRows(data.rows.map(x => {

            let row: IResourceDataEx = {
                ...x,
                highlights: {},
            };

            for (let i = 0; i < props.columns.length; i++ ){
                let col = props.columns[i];
                if (!col.searched)
                    continue;

                var value = row[col.id];
                let highlightCol = Highlight(value, props.search);
                row.highlights[col.id] = highlightCol;
            };

            return row;
        }))

    }, [data]);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: { indeterminate: boolean,  selectedAll: boolean }) => {

        const newSelecteds = { ...props.selected };
        for (var i = 0; i < rows.length; i++) {
            let row = rows[i];
            if (event.selectedAll) {
                delete newSelecteds[row.id];
            }
            else {
                newSelecteds[row.id] = {
                    value: row.id.toString(),
                    label: row.name,
                    kind: props.kind,
                };
              
            }
        }
        props.setSelected(newSelecteds);
    };

    const handleClick = (event: React.MouseEvent<unknown>, row: IResourceData) => {
        let newSelecteds: { [key: number]: OptionType } = {};
        if (props.multiple) {
            newSelecteds = { ...props.selected };

            if (newSelecteds[row.id]) {
                delete newSelecteds[row.id];
            }
            else {
                newSelecteds[row.id] = {
                    value: row.id.toString(),
                    label: row.label,
                    kind: props.kind,
                };
            }
        }
        else {
            let isSelected = !!props.selected[row.id];
            if (isSelected) {
                newSelecteds = {};
            }
            else {
                newSelecteds = {
                    [row.id]: {
                        value: row.id.toString(),
                        label: row.label,
                        kind: props.kind,
                    }
                };
            }
        }

        props.setSelected(newSelecteds);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        props.setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        props.setPage(0);
    };

    const isSelected = (id: number) => !!props.selected[id];

    const selctedCount = rows.filter(x => isSelected(x.id)).length;

    return <div className="resources-autocomplete-search">
        {isError && <p className="text-danger">Nieoczekiwany b��d w czasie pobrania zasob�w.</p>}
        {data?.errorMessage && <p className="text-danger">{data.errorMessage}</p>}
        {data?.warningsMessages?.map((x, i) => <p key={i} className="text-warning">{x}</p>)}
        <Paper className={classes.paper}>
            <TableContainer className={classes.container} >
                <Table
                    stickyHeader
                    className={classes.table}
                    aria-labelledby="tableTitle"
                    aria-label="enhanced table"
                >
                    <EnhancedTableHead
                        classes={classes}
                        numSelected={selctedCount}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onRequestSort={handleRequestSort}
                        rowCount={rows.length}

                        headCells={props.columns}
                        multiple={props.multiple}
                        isLoading={isLoading}
                    />
                    <TableBody>
                        {rows.map((row, index) => {
                                const isItemSelected = isSelected(row.id);

                                return (
                                    <TableRow
                                        hover
                                        onClick={(event) => handleClick(event, row)}
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        selected={isItemSelected}
                                        className={clsx({
                                            "row-selected": isItemSelected  
                                        })}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                            />
                                        </TableCell>
                                        {props.columns?.map((column, i) => <TableCell
                                            key={column.id}
                                            padding={i == 0 ? "none" : undefined}
                                            align={column.numeric ? 'right' : 'left'}
                                            className={column.className}
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            <DisplayCell row={row} column={column} />
                                        </TableCell>)}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={props.rowsPerPageOptions ?? [10, 50, 100, { value: 100000, label: strings.get("all") }]}
                component="div"
                count={data?.totalSize ?? 0}
                rowsPerPage={rowsPerPage}
                page={props.page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                labelRowsPerPage={strings.get("rowsPerPage")}
            />
        </Paper>
    </div>
}

interface IProps {
    value: OptionType[],

    onSelect: (value: OptionType[]) => void,

    filters: string,
    kind: 'Application' | 'ServiceAsset' | 'BusinessProcess' | 'DataSet' | 'Employee' | 'PersonProfile',
    multiple?: boolean,
   
    columns: HeadCell[],
    title: string,

    className: string,

    rowsPerPageOptions: Array<number | { value: number; label: string }>
}

export default function ResourcesAutocompleteSearch(props: IProps)
{
    const { t } = useTranslation();
    const [openSearch, setOpenSearch] = React.useState(false);
    const [selected, setSelected] = React.useState<{ [key: number]: OptionType }>({});
    const [page, setPage] = React.useState(0);

    const [search, setSearch] = React.useState("");
    const [searchLazy, setSearchLazy] = React.useState("");
    const debouncedSearchTerm = useDebounce(search, 1500);

    React.useEffect(() => {
        setPage(0);
        setSearchLazy(search);
    }, [debouncedSearchTerm]);

    React.useEffect(() => {

        let newSelected = {};

        if (props.value) {
            for (var i = 0; i < props.value.length; i++) {
                let v = props.value[i];
                if (v) {
                    newSelected[parseInt(v.value)] = v;
                };
            }
        }

        setSelected(newSelected);
    }, [props.value]);

    const handleSelectClick = function () {
        props.onSelect(Object.values(selected));
        setOpenSearch(false);
    }


    let title = props.title ?? strings.get("selectResource");
    return <>
        <SearchButton className={props.className} title={props.title} onClick={() => setOpenSearch(true)} />
        <Dialog
            open={openSearch}
            onClose={() => setOpenSearch(false)}
            fullWidth
            maxWidth="md"
            disableBackdropClick
        >
            <DialogTitle>
                {title}
                <TextField
                    variant="outlined"
                    placeholder={t("common.action.search") + "..."}

                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    style={{
                        float: "right"
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start" > <SearchIcon color="disabled" /></InputAdornment>
                    }}
                />
            </DialogTitle>
            <DialogContent>
                <EnhancedTable
                    filters={props.filters}
                    kind={props.kind}
                    onClose={() => setOpenSearch(false)}
                    open={openSearch}
                    multiple={props.multiple}
                    columns={props.columns ?? []}

                    selected={selected}
                    setSelected={setSelected}
                    search={searchLazy}

                    page={page}
                    setPage={setPage}
                    rowsPerPageOptions={props.rowsPerPageOptions}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenSearch(false)} color="default" variant="contained">
                    {t("common.action.cancel")}
                </Button>
                <Button onClick={handleSelectClick} color="primary" variant="contained">
                    {t("common.action.select")}
                </Button>
            </DialogActions>
        </Dialog>
    </>
}
